<script setup>

import {ref, inject, computed, toRefs, reactive, onMounted, onUnmounted} from 'vue';

// Components
import StaticWidget from "@/components/common/other/StaticWidget";
import DimensionsEntry from "@/components/common/other/DimensionsEntry";
import MultiSearchDropDown from "@/components/common/input/MultiSearchDropDown";
import Button from "@/components/common/input/Button";
import SectionTile from "@/components/bookings/SectionTile";
import CheckBox from "@/components/common/input/CheckBox";
import DateInput from "@/components/common/input/DateInput";
import NumberOnlyInput from "@/components/common/input/NumberOnlyInput";
import TextArea from "@/components/common/input/TextArea";
import StaticTooltip from "@/components/common/other/StaticTooltip";
import BookingSummaryDisplay from "@/components/bookings/BookingSummaryDisplay";
import DropDownTextArea from "@/components/common/input/DropDownTextArea";
import ShipperAddress from "@/components/common/other/ShipperAddress";
import Modal from "@/components/common/other/Modal.vue";
import ShipperDetail from "@/classes/bookings/ShipperDetail";
import ConsigneeDetail from "@/classes/bookings/ConsigneeDetail";
import ShipperContacts from "@/components/common/other/ShipperContacts.vue";
import ConsigneeAddress from "@/components/common/other/ConsigneeAddress.vue";
import ConsigneeContact from "@/components/common/other/ConsigneeContact.vue";
import LoadingSpinner from "@/components/common/other/LoadingSpinner.vue";

// Classes
import API from "@/classes/global/API";
import Notification from "@/classes/component-classes/Notification";
import ContactDetail from "@/classes/bookings/ContactDetail";
import FreightBookingValidation from "@/classes/bookings/FreightBookingValidation";
import CourierBookingValidation from "@/classes/bookings/CourierBookingValidation";
import FreightBooking from "@/classes/viewmodels/FreightBooking";
import CourierBooking from "@/classes/viewmodels/CourierBooking";

// Composables
import {useGetValidatedFields} from "@/composables/common/useGetValidatedFields";
import {useValidateRequiredFields} from "@/composables/common/useValidateRequiredFields";
import {useCheckFieldsForData} from "@/composables/common/useCheckFieldsForData";
import {useCreateSubstringText} from "@/composables/common/useCreateSubstringText";
import {useTimeGenerator} from "@/composables/common/useTimeGenerator";
import {useMapBookingSave} from "@/composables/bookings/useMapBookingSave";
import {useGetChargeableWeight} from "@/composables/bookings/useGetChargeableWeight";
import {useMapShipperAddress} from "@/composables/bookings/useMapShipperAddress";
import {useValidateContact} from "@/composables/bookings/useValidateContact";

import {useStore} from "vuex";
import {useRoute, useRouter} from "vue-router";
import {useMapConsigneeAddress} from "@/composables/bookings/useMapConsigneeAddress";
import {useClearShipperContact} from "@/composables/bookings/useClearShipperContact";
import {useClearConsigneeContact} from "@/composables/bookings/useClearConsigneeContact";
import {useMapShipperSave} from "@/composables/bookings/useMapShipperSave";
import {useMapConsigneeSave} from "@/composables/bookings/useMapConsigneeSave";
import {useProcessUploadedFiles} from "@/composables/common/useProcessUploadFiles";
import {useTileSelection} from "@/composables/bookings/useTileSelection";
import {useMapContact} from "@/composables/bookings/useMapContact";

const router = useRouter();
const store = useStore();
const route = useRoute();

const validationViewModel = inject("validationViewModel");

const props = defineProps({

  modelValue: {},
  validationViewModelSection: '',
  tileColor: {type: String, default: '#3a6b82'}

})


const emits = defineEmits(['update:modelValue'])

const uploadButton = ref();
const uploadFileSizeError = ref(false);
const uploadFilesSpinner = ref(false);

const updateShipperContacts = computed(() => {

  if (props.modelValue.shipper.shipperContactFullName || props.modelValue.shipper.shipperContacts) {
    return Math.random().toString(36).substring(2, 15);
  }
})

const updateConsigneeContacts = computed(() => {

  if (props.modelValue.consignee.consigneeContactFullName || props.modelValue.consignee.consigneeContacts) {
    return Math.random().toString(36).substring(2, 15);
  }
})

const updateFileInput = computed(() => {

  return props.modelValue.files.length
})

const isEditCourierBooking = computed(() => {

  // This is to hide the inputs and summary on edit, for the email only data not sent to db
  if (route.params.id !== 'new' && route.params.section === 'courierBooking') {

    return true;
  }

})

const getPlaceholder = computed(() => getAddressPlaceholder());

const allowWeightEntry = computed(() => {

  return props.modelValue.dimensions.multiLineDims.length > 0;
})

let uploadFileSizeErrorMessage = ref();
let validationSection = ref();

onMounted(() => {

  if (route.params.id === 'new' && props.modelValue.id !== 0) {

    handleNewBooking(route.params.section);

  }

  // This is required to manage the state of the validation object for edit and new bookings.
  // It prevents the validation state being held when coming out of an edit and into a new freight booking.
  // Checks the url for an edit id number to ensure you have just come from the edit screen,
  // then resets validation. If you are going back and forth with a new booking,
  // the original state is held and the validation is not reset.
  router.beforeEach((to, from) => {

    const isEditId = /^\d+$/.test(from.params.id)

    if (isEditId && props.validationViewModelSection === 'freightBooking') {

      resetTileValidationIcon();

      validationViewModel.value[props.validationViewModelSection] = new FreightBookingValidation();
    }

    if (isEditId && props.validationViewModelSection === 'courierBooking') {

      resetTileValidationIcon();

      validationViewModel.value[props.validationViewModelSection] = new CourierBookingValidation();
    }

  })

  prepShipmentMetaData();

});

const insuranceTypes = [
  {
    id: '1',
    value: 'Insured By Customer'
  },
  {
    id: '2',
    value: 'Insured By Carrier'
  },
]

function getAddressPlaceholder() {

  return require(`@/assets/address_placeholder.png`);
}

function getChargeableWeightCalculation(totalWeight) {

  props.modelValue.unitOfMeasureSwitch ? props.modelValue.dimensions.unitOfMeasure = 'imperial' :
      props.modelValue.dimensions.unitOfMeasure = 'metric';

  const weightTotal = totalWeight.value; // copy computed value

  useGetChargeableWeight(route, weightTotal, props.modelValue, route.params.section);


}

function mapShipperAddress(data) {

  useClearShipperContact(props.modelValue);
  useMapShipperAddress(data, props.modelValue);

}

function mapConsigneeAddress(data) {

  useClearConsigneeContact(props.modelValue);
  useMapConsigneeAddress(data, props.modelValue);

}

function mapShipperSaveDetails(isNewContact = false) {

  useMapShipperSave(props.modelValue, route, isNewContact);

}

function mapConsigneeSaveDetails(isNewContact = false) {

  useMapConsigneeSave(props.modelValue, route, isNewContact);

}

function handleTileSelection(tile) {

  let currentTile = useTileSelection(tile, props.modelValue, validationViewModel);

  //Check the tile currently active (for when moving from tile to tile)
  if (tile !== 'customs') checkFields(currentTile[0]);

  //Check the new active tile
  if (tile !== 'customs') checkFields(tile);

}

function handleCustomsStatus(event, status) {

  if (event.target.checked) {

    props.modelValue.customs.push(status);
    props.modelValue.tileValidation.customs = true;

  } else {

    props.modelValue.customs.splice(props.modelValue.customs.findIndex(x => x == status), 1)

  }

}

function handleValidateAndClose(tile) {

  if (tile === 'customs') {

    let customsValid = validateCustomsStatus();

    if (customsValid) handleTileSelection('customs');

    return
  }

  let validationResult = validate(tile);

  if (validationResult) handleTileSelection(tile);

  return validationResult

}

function handleRemoveDocument(index) {

  props.modelValue.files.splice(index, 1);

}

function handleUpload() {

  uploadButton.value.click()
}

function handleShipperSave() {

  if (validate('shipper')) mapShipperSaveDetails();

}

function handleConsigneeSave(isUpdate) {

  if (validate('consignee'))  mapConsigneeSaveDetails();

}

function handleNewBooking(section) {

  router.push({name: 'booking', params: {owner: store.state.owner, section: section, id: 'new'}});

  if (section === 'freightBooking') {

    emits('update:modelValue', new FreightBooking());

  } else {

    emits('update:modelValue', new CourierBooking());
  }

  prepShipmentMetaData();

}

function validateContact(tile) {

  return useValidateContact(props.modelValue, tile);

}

function selectBillingPartyId(selection) {

  props.modelValue.shipment.shipmentBillingPartyId = selection.id;
}

function handleTermsDescription(ddlData) {

  props.modelValue.consignment.termsDescription = ddlData.termDetail;

}

function validateCustomsStatus() {

  let customsValid = true;

  const validateStatus = useGetValidatedFields(props.modelValue.metaData.validatedFields, 'customsStatus');
  const validateComments = useGetValidatedFields(props.modelValue.metaData.validatedFields, 'customsComments');

  if (validateStatus && props.modelValue.customs.length === 0) {

    props.modelValue.tileValidation['customs'] = false;

    customsValid = false;

  } else if (validateComments && !props.modelValue.customsComments) {

    validationViewModel.value[props.validationViewModelSection].customsComments.value = true;
    props.modelValue.tileValidation['customs'] = false;

    customsValid = false;

  } else {

    props.modelValue.tileValidation['customs'] = true;
  }

  return customsValid;
}

function checkFields(tile) {

  if (tile !== 'customs' && useCheckFieldsForData(props.modelValue[tile])) {

    props.modelValue.tileValidation[tile] = validate(tile);

  }

}

function validate(tile) {

  if(tile !== 'customs') {

    let fieldsValid = useValidateRequiredFields(props.modelValue[tile],
        validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields);

    props.modelValue.tileValidation[tile] = fieldsValid;

    return fieldsValid
  } else {

    handleValidateAndClose()
  }

}

function resetTileValidationIcon() {

  for (let each in props.modelValue.tileValidation) {

    props.modelValue.tileValidation[each] = true;

  }
}

async function prepShipmentMetaData() {

  if (route.params.section === 'freightBooking') validationSection = 'freightBookingValidatedFields';
  if (route.params.section === 'courierBooking') validationSection = 'courierBookingValidatedFields';

  const response = await API.getShipperMetaData(route, validationSection);

  if (response) props.modelValue.metaData = response;

}

async function validateSubmit(bookingType) {

  props.modelValue.tileValidation['shipper'] = validate('shipper');
  props.modelValue.tileValidation['consignee'] = validate('consignee');
  props.modelValue.tileValidation['dimensions'] = validate('dimensions');

  if (props.validationViewModelSection === 'freightBooking') {
    props.modelValue.tileValidation['shipment'] = validate('shipment');

  } else {

    // Must be CourierBooking
    props.modelValue.tileValidation['consignment'] = validate('consignment');
    props.modelValue.tileValidation['customs'] = validateCustomsStatus();
  }

  for (let each in props.modelValue.tileValidation) {

    if (props.modelValue.tileValidation[each] === false) {

      return false

    }

  }

  // Validation Passed for all sections

  let response = await API.saveBooking(useMapBookingSave(props.modelValue, props.validationViewModelSection), bookingType, route);

  if (response) {

    try {

      props.modelValue.id = response.id;
      props.modelValue.jobNumber = response.jobNumber;

      // Add the new returned dims with id's to the model, remove unitOfMeasure as not used for UI and would run through the useCheckFieldsForData method
      // with the value sent back from server (metric / imperial, text) and trigger the tile to show the data present sheild
      props.modelValue.dimensions = {... props.modelValue.dimensions, ...response.dimensions};

      // Ensure always string value for total weight to avoid breaking calcs
      props.modelValue.dimensions.totalWeight = props.modelValue.dimensions.totalWeight.toString();
      delete props.modelValue.dimensions.unitOfMeasure;

      store.state.banners.success = new Notification(true, 'Booking Saved Successfully');
    } catch (error) {

      // TODO Log Error
    }

  }

}

async function handleUploadedFiles(files) {

  uploadFilesSpinner.value = true;

  try{
    const fileArray = Array.from(files);  

    // Scan all files
    const results = await Promise.all(
      fileArray.map(async (file) => {
        const scanResult = await API.FileVirusScan(file);
        return { file, scanResult };
      })
    );

    // Keep only the files that passed
    const safeFiles = results
      .filter(r => r.scanResult === 'Passed')
      .map(r => r.file);

    uploadFileSizeError.value = false;

    let fileUploadOk = await useProcessUploadedFiles(safeFiles, props.modelValue);

    if (!fileUploadOk.value) {

      uploadFileSizeError.value = true;
      uploadFileSizeErrorMessage.value = fileUploadOk.file.name + ' ' + 'is too large to upload - max 28MB'
    }

  } finally {
    uploadFilesSpinner.value = false;
  }

}

function addNewShipperAddress() {

  props.modelValue.isEditShipperAddress = false;

  props.validationViewModelSection === 'freightBooking' ?
      validationViewModel.value.freightBooking = new FreightBookingValidation() :
      validationViewModel.value.courierBooking = new CourierBookingValidation();

  props.modelValue.newShipperCompany = new ShipperDetail();
  props.modelValue.openShipperModal = true;
}

function addNewConsigneeAddress() {

  props.modelValue.isEditConsigneeAddress = false;

  props.validationViewModelSection === 'freightBooking' ?
      validationViewModel.value.freightBooking = new FreightBookingValidation() :
      validationViewModel.value.courierBooking = new CourierBookingValidation();

  props.modelValue.newConsigneeCompany = new ConsigneeDetail();
  props.modelValue.openConsigneeModal = true;
}

function editShipperAddress() {

  props.modelValue.isEditShipperAddress = true;

  props.modelValue.openShipperModal = true;
  props.modelValue.newShipperCompany =  JSON.parse(JSON.stringify(props.modelValue.shipper));
}

function editConsigneeAddress() {

  props.modelValue.isEditConsigneeAddress = true;

  props.modelValue.openConsigneeModal = true;
  props.modelValue.newConsigneeCompany =  JSON.parse(JSON.stringify(props.modelValue.consignee));
}


function useUnSavedShipperAddress() {

  if (useValidateRequiredFields(props.modelValue.newShipperCompany,
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    const addressFields = ['shipperName', 'shipperAddressLine1', 'shipperAddressLine2', 'shipperCity',
      'shipperPostCode', 'shipperCounty', 'shipperCountry', 'shipperVatNumber'];

    if(props.modelValue.newShipperCompany.id !== 0) {

      // Must be a selected address record, check for any changes and remove id if so
      addressFields.forEach(field => {

          if (props.modelValue.newShipperCompany[field] !== props.modelValue.shipper[field]) {

            return props.modelValue.newShipperCompany.id = 0;
          }
      })
    }

      props.modelValue.shipper = props.modelValue.newShipperCompany;
      props.modelValue.openShipperModal = false;
  }
}

function useUnSavedConsigneeAddress() {

  if (useValidateRequiredFields(props.modelValue.newConsigneeCompany,
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    const addressFields = ['consigneeName', 'consigneeAddressLine1', 'consigneeAddressLine2', 'consigneeCity',
      'consigneePostCode', 'consigneeCounty', 'consigneeCountry', 'consigneeVatNumber'];

    if(props.modelValue.newConsigneeCompany.id !== 0) {

      // Must be a selected address record, check for any changes and remove id if so
      addressFields.forEach(field => {

        if(props.modelValue.newConsigneeCompany.id !== 0) {

          // Must be a selected address record, check for any changes and remove id if so
          addressFields.forEach(field => {

            if (props.modelValue.newConsigneeCompany[field] !== props.modelValue.consignee[field]) {

              return props.modelValue.newConsigneeCompany.id = 0;
            }
          })
        }
      })
    }

    props.modelValue.consignee = props.modelValue.newConsigneeCompany;
    props.modelValue.openConsigneeModal = false;
  }
}

function closeShipperModel() {

  validationViewModel.value[props.validationViewModelSection].shipperName.value = false;
  props.modelValue.newShipperCompany = new ShipperDetail();

}

function closeConsigneeModel() {

  validationViewModel.value[props.validationViewModelSection].consigneeName.value = false;
  props.modelValue.newShipperCompany = new ShipperDetail();

}

function handleNewCompanySave(addressType) {

  if (useValidateRequiredFields(props.modelValue[addressType],
      validationViewModel.value[props.validationViewModelSection], props.modelValue.metaData.validatedFields)) {

    if(addressType === 'newShipperCompany') {

      mapNewShipperAddress();
      handleShipperSave();

    } else {

      mapNewConsigneeAddress();
      handleConsigneeSave(false);
    }
  }
}

function mapNewShipperAddress() {

  props.modelValue.shipper.shipperName = props.modelValue.newShipperCompany.shipperName;
  props.modelValue.shipper.shipperAddressLine1 = props.modelValue.newShipperCompany.shipperAddressLine1;
  props.modelValue.shipper.shipperAddressLine2 = props.modelValue.newShipperCompany.shipperAddressLine2;
  props.modelValue.shipper.shipperCity = props.modelValue.newShipperCompany.shipperCity;
  props.modelValue.shipper.shipperPostcode = props.modelValue.newShipperCompany.shipperPostcode;
  props.modelValue.shipper.shipperCounty = props.modelValue.newShipperCompany.shipperCounty;
  props.modelValue.shipper.shipperCountry = props.modelValue.newShipperCompany.shipperCountry;
  props.modelValue.shipper.shipperVatNumber = props.modelValue.newShipperCompany.shipperVatNumber;
}

function mapNewConsigneeAddress() {

  props.modelValue.consignee.consigneeName = props.modelValue.newConsigneeCompany.consigneeName;
  props.modelValue.consignee.consigneeAddressLine1 = props.modelValue.newConsigneeCompany.consigneeAddressLine1;
  props.modelValue.consignee.consigneeAddressLine2 = props.modelValue.newConsigneeCompany.consigneeAddressLine2;
  props.modelValue.consignee.consigneeCity = props.modelValue.newConsigneeCompany.consigneeCity;
  props.modelValue.consignee.consigneePostcode = props.modelValue.newConsigneeCompany.consigneePostcode;
  props.modelValue.consignee.consigneeCounty = props.modelValue.newConsigneeCompany.consigneeCounty;
  props.modelValue.consignee.consigneeCountry = props.modelValue.newConsigneeCompany.consigneeCountry;
  props.modelValue.consignee.consigneeVatNumber = props.modelValue.newConsigneeCompany.consigneeVatNumber;
}

function addNewShipperContact() {

  clearContactValidation('shipper');

  useClearShipperContact(props.modelValue.newShipperCompany);

  props.modelValue.openShipperContactModal = true;
}

function addNewConsigneeContact() {

  clearContactValidation('consignee');

  useClearConsigneeContact(props.modelValue.newConsigneeCompany);

  props.modelValue.openConsigneeContactModal = true;
}

function clearContactValidation(addressType) {

  if(addressType === 'shipper') {

    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.value = false;
    validationViewModel.value[props.validationViewModelSection].shipperContactFirstName.value = false

  } else {

    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.value = false;
    validationViewModel.value[props.validationViewModelSection].consigneeContactFirstName.value = false;
  }

}

function useUnSavedShipperContact() {

  let results = validateShipperContact()

  if(results.firstNameValid && results.emailValid) {

    const contactFields = ['shipperContactTitle', 'shipperContactFirstName',
      'shipperContactLastName', 'shipperContactPhone', 'shipperContactEmail', 'shipperContactDepartment'
    ]

    contactFields.forEach(field => {

      if(props.modelValue.newShipperCompany[field] !== props.modelValue.shipper[field]) {

        props.modelValue.newShipperCompany.shipperContactId = 0;
      }

    })

    mapShipperContact();
  }
}

function validateShipperContact() {

  let firstNameValid = true;

  if (!props.modelValue.newShipperCompany.shipperContactFirstName) {

    validationViewModel.value[props.validationViewModelSection].shipperContactFirstName.value = true;

    firstNameValid = false;
  }

  let emailValid = true;

  if(props.modelValue.newShipperCompany.shipperContactEmail) {

    emailValid = store.state.emailRegExCheck.test(props.modelValue.newShipperCompany.shipperContactEmail);

    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.messageNumber = 1
    validationViewModel.value[props.validationViewModelSection].shipperContactEmail.value = !emailValid;

  }

  return {firstNameValid, emailValid}
}

function saveNewShipperContact(isUpdate) {

  let result = validateShipperContact();

  if (result.firstNameValid && result.emailValid) {

    mapShipperContact();

    if(isUpdate) {

      let contactToEdit = props.modelValue.shipper.shipperContacts.find(c => c.id === props.modelValue.shipper.shipperContactId);

      useMapContact(contactToEdit, props.modelValue, true, false);

    } else {

      let newContact = new ContactDetail();

      useMapContact(newContact, props.modelValue, true, true);
    }

    handleShipperSave();
  }
}

function useUnSavedConsigneeContact() {

  let results = validateConsigneeContact();

  if (results.firstNameValid && results.emailValid) {

    const contactFields = ['consigneeContactTitle', 'consigneeContactFirstName',
      'consigneeContactLastName', 'consigneeContactPhone', 'consigneeContactEmail', 'consigneeContactDepartment'
    ]

    contactFields.forEach(field => {

      if(props.modelValue.newConsigneeCompany[field] !== props.modelValue.consignee[field]) {

        props.modelValue.newConsigneeCompany.consigneeContactId = 0;
      }

    })

    mapConsigneeContact();
  }
}

function validateConsigneeContact() {

  let firstNameValid = true;

  if (!props.modelValue.newConsigneeCompany.consigneeContactFirstName) {

    validationViewModel.value[props.validationViewModelSection].consigneeContactFirstName.value = true;

    firstNameValid = false;
  }

  let emailValid = true;

  if (props.modelValue.newConsigneeCompany.consigneeContactEmail) {

    emailValid = store.state.emailRegExCheck.test(props.modelValue.newConsigneeCompany.consigneeContactEmail);

    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.messageNumber = 1
    validationViewModel.value[props.validationViewModelSection].consigneeContactEmail.value = !emailValid;

  }

  return {firstNameValid, emailValid};
}

  function saveNewConsigneeContact(isUpdate) {

    let results = validateConsigneeContact();

    if(results.firstNameValid && results.emailValid) {

      mapConsigneeContact();

      if (isUpdate) {

        let contactToEdit = props.modelValue.consignee.consigneeContacts.find(c => c.id === props.modelValue.consignee.consigneeContactId);

        useMapContact(contactToEdit, props.modelValue, false, false);

      } else {

        let newContact = new ContactDetail();

        useMapContact(newContact, props.modelValue, false, true);
      }

      handleConsigneeSave();
    }
  }

  function editShipperContact() {

    props.modelValue.openShipperContactModal = true;

    clearContactValidation('shipper');

    props.modelValue.newShipperCompany.shipperContactId = props.modelValue.shipper.shipperContactId;
    props.modelValue.newShipperCompany.shipperContactTitle = props.modelValue.shipper.shipperContactTitle;
    props.modelValue.newShipperCompany.shipperContactFirstName = props.modelValue.shipper.shipperContactFirstName;
    props.modelValue.newShipperCompany.shipperContactLastName = props.modelValue.shipper.shipperContactLastName;
    props.modelValue.newShipperCompany.shipperContactPhone = props.modelValue.shipper.shipperContactPhone;
    props.modelValue.newShipperCompany.shipperContactEmail = props.modelValue.shipper.shipperContactEmail;
    props.modelValue.newShipperCompany.shipperContactDepartment = props.modelValue.shipper.shipperContactDepartment;
  }

  function mapShipperContact() {

    props.modelValue.shipper.shipperContactFullName = props.modelValue.newShipperCompany.shipperContactTitle + ' ' +
        props.modelValue.newShipperCompany.shipperContactFirstName + ' '
        + props.modelValue.newShipperCompany.shipperContactLastName;

    props.modelValue.shipper.shipperContactId = props.modelValue.newShipperCompany.shipperContactId;
    props.modelValue.shipper.shipperContactTitle = props.modelValue.newShipperCompany.shipperContactTitle;
    props.modelValue.shipper.shipperContactFirstName = props.modelValue.newShipperCompany.shipperContactFirstName;
    props.modelValue.shipper.shipperContactLastName = props.modelValue.newShipperCompany.shipperContactLastName;
    props.modelValue.shipper.shipperContactPhone = props.modelValue.newShipperCompany.shipperContactPhone;
    props.modelValue.shipper.shipperContactEmail = props.modelValue.newShipperCompany.shipperContactEmail;
    props.modelValue.shipper.shipperContactDepartment = props.modelValue.newShipperCompany.shipperContactDepartment;
  }

  function mapConsigneeContact() {

    props.modelValue.consignee.consigneeContactFullName = props.modelValue.newConsigneeCompany.consigneeContactTitle + ' ' +
        props.modelValue.newConsigneeCompany.consigneeContactFirstName + ' '
        + props.modelValue.newConsigneeCompany.consigneeContactLastName;

    props.modelValue.consignee.consigneeContactId = props.modelValue.newConsigneeCompany.consigneeContactId;
    props.modelValue.consignee.consigneeContactTitle = props.modelValue.newConsigneeCompany.consigneeContactTitle;
    props.modelValue.consignee.consigneeContactFirstName = props.modelValue.newConsigneeCompany.consigneeContactFirstName;
    props.modelValue.consignee.consigneeContactLastName = props.modelValue.newConsigneeCompany.consigneeContactLastName;
    props.modelValue.consignee.consigneeContactPhone = props.modelValue.newConsigneeCompany.consigneeContactPhone;
    props.modelValue.consignee.consigneeContactEmail = props.modelValue.newConsigneeCompany.consigneeContactEmail;
  }

  function editConsigneeContact() {

    props.modelValue.openConsigneeContactModal = true;

    clearContactValidation('consignee');

    props.modelValue.newConsigneeCompany.consigneeContactId = props.modelValue.consignee.consigneeContactId;
    props.modelValue.newConsigneeCompany.consigneeContactTitle = props.modelValue.consignee.consigneeContactTitle;
    props.modelValue.newConsigneeCompany.consigneeContactFirstName = props.modelValue.consignee.consigneeContactFirstName;
    props.modelValue.newConsigneeCompany.consigneeContactLastName = props.modelValue.consignee.consigneeContactLastName;
    props.modelValue.newConsigneeCompany.consigneeContactPhone = props.modelValue.consignee.consigneeContactPhone;
    props.modelValue.newConsigneeCompany.consigneeContactEmail = props.modelValue.consignee.consigneeContactEmail;
  }

</script>

<template class="cmp-multi-booking">
  <div>
    <StaticWidget
        :title="props.validationViewModelSection === 'freightBooking' ? 'Freight Booking Details' : 'Courier Booking Details'"
        height="740">
      <template #extra-content>
        <div class="cmp-multi-booking_required-field-message"><span class="global-required-field"></span>
          indicates the field is required
        </div>
        <div v-if="/^\d+$/.test(route.params.id)" class="cmp-multi-booking_edit-message">
         <img src="@/assets/icons/akar/akar-icons--edit.png" height="20" width="20">
          <span style="font-size: 16px;">Edit Booking #<span>{{ route.params.id }}</span></span>
        </div>
        <div v-else-if="props.modelValue.id">
          <span style="font-weight: bold; margin-left: 50px; font-size: 13px;">Job Number: </span> #<span>{{ props.modelValue.id }}</span>
        </div>
      </template>
      <template #right-side>
        <div>
          <Button
              v-if="props.modelValue.id === 0"
              text="Save & Submit"
              icon="<i class='fa-light fa-floppy-disk'></i>"
              @click="validateSubmit(props.validationViewModelSection)">
          </Button>
          <Button
              v-if="props.modelValue.id !== 0"
              text="Update"
              type="success"
              icon="<i class='fa-light fa-floppy-disk'></i>"
              @click="validateSubmit(props.validationViewModelSection)">
          </Button>
          <Button
              v-if="props.modelValue.id !== 0"
              text="New Booking"
              icon="<i class='fa-regular fa-file'></i>"
              @click="handleNewBooking(props.validationViewModelSection)"
              type="toolbar"></Button>
        </div>
      </template>
      <div class="cmp-multi-booking_wrapper">
        <div class="cmp-multi-booking_tile-wrapper">
          <SectionTile
              @select="handleTileSelection('shipper')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.shipper"
              v-model:tileSelectionModel="props.modelValue.tileSelection.shipper"
              v-model:validationProp="props.modelValue.tileValidation.shipper">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/shippers_globe.png"></template>
            <template #title>Shipper</template>
            <template #description>Address & Contact</template>
          </SectionTile>
          <SectionTile
              @select="handleTileSelection('consignee')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.consignee"
              v-model:tileSelectionModel="props.modelValue.tileSelection.consignee"
              v-model:validationProp="props.modelValue.tileValidation.consignee">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/consignee_clipboard.png"></template>
            <template #title>Consignee</template>
            <template #description>Address & Contact</template>
          </SectionTile>
          <SectionTile
              v-if="props.validationViewModelSection === 'freightBooking'"
              @select="handleTileSelection('shipment')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.shipment"
              v-model:tileSelectionModel="props.modelValue.tileSelection.shipment"
              v-model:validationProp="props.modelValue.tileValidation.shipment">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/truck_loading.png"></template>
            <template #title>Shipment</template>
            <template #description>Details Regarding The Shipment</template>
          </SectionTile>
          <SectionTile
              v-else
              @select="handleTileSelection('consignment')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.consignment"
              v-model:tileSelectionModel="props.modelValue.tileSelection.consignment"
              v-model:validationProp="props.modelValue.tileValidation.consignment">
            <template #icon>
              <img class="global-tile-icon"
                   style="height: 55px; width: 55px;"
                   src="@/assets/icons/courier.png">
            </template>
            <template #title>Consignment Details</template>
            <template #description>References & Delivery</template>
          </SectionTile>
          <SectionTile
              @select="handleTileSelection('dimensions')"
              :tileColor="tileColor"
              v-model:viewModel="props.modelValue.dimensions"
              v-model:tileSelectionModel="props.modelValue.tileSelection.dimensions"
              v-model:validationProp="props.modelValue.tileValidation.dimensions">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/dimensions.png"></template>
            <template #title>Dimensions</template>
            <template #description>Packaging & Item Detail</template>
          </SectionTile>
          <SectionTile
              v-model:tileSelectionModel="props.modelValue.tileSelection.documents"
              :tileColor="tileColor"
              @select="handleTileSelection('documents')" :useIcons="false">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/upload_docs.png"></template>
            <template #title>Upload Docs</template>
            <template #description>Add Docs To The Booking</template>
          </SectionTile>
          <SectionTile
              v-if="store.state.userSettings.showCourierCustoms && props.validationViewModelSection === 'courierBooking'"
              :tileColor="tileColor"
              v-model:tileSelectionModel="props.modelValue.tileSelection.customs"
              v-model:validationProp="props.modelValue.tileValidation.customs"
              v-model:viewModel="props.modelValue.customsSelect"
              @select="handleTileSelection('customs')">
            <template #icon><img class="global-tile-icon" src="@/assets/icons/passport.png"></template>
            <template #title>Customs</template>
            <template #description>Status & Comments</template>
          </SectionTile>
        </div>
        <div v-show="props.modelValue.tileSelection.shipper" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container-address cmp-multi-booking_add-border">
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ShipperAddress
                  @addNewShipperAddress="addNewShipperAddress()"
                  @editShipperAddress="editShipperAddress()"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].shipperName"
                  v-model="props.modelValue">
              </ShipperAddress>
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <img height="28" width="28" src="@/assets/icons/akar/akar-icons--people-group.png">
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <shipperContacts
                  @addNewShipperContact="addNewShipperContact()"
                  @editShipperContact="editShipperContact()"
                  v-model:updateShipperContacts="updateShipperContacts"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].shipperContactFullName"
                  v-model="props.modelValue">
              </shipperContacts>
            </div>
          </div>
        </div>

        <div v-show="props.modelValue.tileSelection.consignee" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container-address cmp-multi-booking_add-border">
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ConsigneeAddress
                  @addNewConsigneeAddress="addNewConsigneeAddress()"
                  @editConsigneeAddress="editConsigneeAddress()"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].consigneeName"
                  v-model="props.modelValue">
              </ConsigneeAddress>
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <img height="28" width="28" src="@/assets/icons/akar/akar-icons--people-group.png">
            </div>
            <div class="cmp-multi-booking_address-layout-wrapper">
              <ConsigneeContact
                  @addNewConsigneeContact="addNewConsigneeContact()"
                  @editConsigneeContact="editConsigneeContact()"
                  v-model:updateConsigneeContacts="updateConsigneeContacts"
                  v-model:validationViewModel="validationViewModel[props.validationViewModelSection].consigneeContactFullName"
                  v-model="props.modelValue">
              </ConsigneeContact>
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.shipment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-truck-ramp-box cmp-multi-booking_tile-icon" style="color: var(--aqua-blue-definition);"></i>
            <div class="global-css_vertical-form-layout">
              <div class="global-flex-layout">
                <TextInput
                    :width="400"
                    v-model="props.modelValue.shipment.shipmentRef"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentRef"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentRef' )"
                    label="Your Ref:">
                </TextInput>
                <CheckBox
                    v-model="props.modelValue.shipment.shipmentRefBolNumber"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentRefBolNumber"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentRefBolNumber' )"
                    label="BOL Number?"
                    :label-width="80">
                </CheckBox>
              </div>
              <TextInput
                  :width="400"
                  v-model="props.modelValue.shipment.shipmentShipperRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentShipperRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentShipperRef' )"
                  label="Shipper Ref:">
              </TextInput>
              <TextInput
                  :width="400"
                  v-model="props.modelValue.shipment.shipmentConsigneeRef"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentConsigneeRef"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentConsigneeRef' )"
                  label="Consignee Ref:">
              </TextInput>
              <TextInput
                  :width="260"
                  v-model="props.modelValue.shipment.shipmentCarrier"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentCarrier"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentCarrier' )"
                  label="Carrier">
              </TextInput>
              <div v-if="props.modelValue.shipment.shipmentBillingParty === 'Third Party'"
                   class="cmp-multi-booking_third-party-area">
                <div class="cmp-multi-booking_third-party-label"
                     :class="{'global-required-field' : useGetValidatedFields(props.modelValue.metaData.validatedFields,
                  'shipmentBillingPartyDetail')}">Third Party Detail
                </div>
                <TextArea
                    v-model="props.modelValue.shipment.shipmentBillingPartyDetail"
                    @input="props.modelValue.shipment.shipmentBillingPartyDetail = $event"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentBillingPartyDetail"
                    :height="53"
                    :width="280">
              </TextArea>
              </div>
              <MultiSearchDropDown
                  v-if="props.modelValue.metaData.serviceTypes.length > 0"
                  :width="260"
                  placeholder="Select or Search...."
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentServiceType' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentServiceType"
                  label="Service Type"
                  v-model="props.modelValue.shipment.shipmentServiceType"
                  :data-source="props.modelValue.metaData.serviceTypes">
              </MultiSearchDropDown>
              <MultiSearchDropDown
                  v-if="props.modelValue.metaData.billingParties.length > 0"
                  :width="260"
                  placeholder="Select or Search...."
                  @select="selectBillingPartyId"
                  @clear="props.modelValue.shipment.shipmentBillingPartyId = 0"
                  :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipmentBillingParty' )"
                  :allowCustomValue="false"
                  :selectPrimitiveValue="true"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentBillingParty"
                  label="Billing Party"
                  v-model="props.modelValue.shipment.shipmentBillingParty"
                  :data-source="props.modelValue.metaData.billingParties">
              </MultiSearchDropDown>
            </div>
            <div class="global-css_vertical-form-layout" style="margin-left: 80px;">
                <DropDownTextArea
                    :width="540"
                    :labelWidth="128"
                    :maxLength="42"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentDescription' )"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentDescription"
                    v-model="props.modelValue.shipment.shipmentDescription"
                    label="Goods Description">
                </DropDownTextArea>
                <DropDownTextArea
                    :width="540"
                    :labelWidth="128"
                    :maxLength="42"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentComments' )"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentComments"
                    v-model="props.modelValue.shipment.shipmentComments"
                    label="Comments">
                </DropDownTextArea>
              <div class="global-flex-layout">
                <DateInput
                    :width="120"
                    :label-width="128"
                    v-model="props.modelValue.shipment.shipmentPickupDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentPickupDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentPickupDate' )"
                    label="Pickup By">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.shipment.shipmentPickupDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <DateInput
                    style="margin-left: 30px;"
                    :width="120"
                    :label-width="80"
                    v-model="props.modelValue.shipment.shipmentDeliveryDate"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentDeliveryDate"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentDeliveryDate' )"
                    label="Delivery Date">
                  <el-date-picker
                      :clearable="false"
                      v-model="props.modelValue.shipment.shipmentDeliveryDate"
                      value-format="DD-MMM-YY"/>
                </DateInput>
                <MultiSearchDropDown
                    style="margin-left: 35px;"
                    :width="65"
                    :label-width="80"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentDeliveryTime' )"
                    placeholder="Select"
                    label="Delivery Time"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentDeliveryTime"
                    v-model="props.modelValue.shipment.shipmentDeliveryTime"
                    :selectPrimitiveValue="true"
                    :disableFilter="true"
                    :data-source="useTimeGenerator(24)">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout">
                <NumberOnlyInput
                    :width="100"
                    :label-width="128"
                    label="Value"
                    :allowDecimal="true"
                    v-model="props.modelValue.shipment.shipmentValue"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentValue"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentValue' )">
                </NumberOnlyInput>
                <MultiSearchDropDown
                    style="margin-left: 60px"
                    :key="props.modelValue.metaData.currencies.length > 0"
                    :width="305"
                    :label-width="60"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentCurrency' )"
                    :allowCustomValue="false"
                    placeholder="Select or Search"
                    label="Currency"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentCurrency"
                    v-model="props.modelValue.shipment.shipmentCurrency"
                    :selectPrimitiveValue="true"
                    :data-source="props.modelValue.metaData.currencies">
                </MultiSearchDropDown>
              </div>
              <div class="global-flex-layout">
                <CheckBox
                    style="padding-top: 2px;"
                    label="Insurance"
                    :label-width="118"
                    @click="props.modelValue.shipment.shipmentInsuranceAmount = ''"
                    v-model="props.modelValue.shipment.shipmentInsurance">
                </CheckBox>
                <NumberOnlyInput
                    :disable="!props.modelValue.shipment.shipmentInsurance"
                    style="margin-left: 165px;"
                    :width="100"
                    :label-width="55"
                    label="Amount"
                    :allowDecimal="true"
                    v-model="props.modelValue.shipment.shipmentInsuranceAmount"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipmentInsuranceAmount"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipmentInsuranceAmount' )">
                </NumberOnlyInput>
              </div>
            </div>
          </div>
<!--          <div class="cmp-multi-booking_drop-down-buttons">-->
<!--            <Button @click="handleTileSelection('shipment')" text="Close" type="toolbar"></Button>-->
<!--            <Button @click="handleValidateAndClose('shipment')" text="OK"></Button>-->
<!--          </div>-->
        </div>
        <div v-if="props.modelValue.tileSelection.consignment" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-person-dolly-empty cmp-multi-booking_tile-icon"
               style="color: var(--aqua-blue-definition); font-size: 60px;"></i>
            <div style="width: 625px; padding-top: 20px;">
              <div class="global-css_vertical-form-layout">
                <TextInput
                    :width="400"
                    :labelWidth="110"
                    v-model="props.modelValue.consignment.customerRef"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRef"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRef' )"
                    label="Your Ref">
                </TextInput>
                <TextInput
                    :width="400"
                    :labelWidth="110"
                    v-model="props.modelValue.consignment.customerRefTwo"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].customerRefTwo"
                    :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customerRefTwo' )"
                    label="Additional Ref">
                </TextInput>
                <div class="global-flex-layout">
                  <DateInput
                      :width="125"
                      :labelWidth="110"
                      v-model="props.modelValue.consignment.collectByDate"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].collectByDate"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'collectByDate' )"
                      label="Pickup By">
                    <el-date-picker
                        :clearable="false"
                        v-model="props.modelValue.consignment.collectByDate"
                        value-format="DD-MMM-YY"/>
                  </DateInput>
                  <MultiSearchDropDown
                      style="margin-left: 80px;"
                      :width="80"
                      :labelWidth="110"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'collectByTime' )"
                      placeholder="Select"
                      label="Pickup Time"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].collectByTime"
                      v-model="props.modelValue.consignment.collectByTime"
                      :selectPrimitiveValue="true"
                      :disableFilter="true"
                      :data-source="useTimeGenerator(24)">
                  </MultiSearchDropDown>
                </div>
                <div class="global-flex-layout" style="margin-top: 0px;">
                  <CheckBox
                      v-model="props.modelValue.consignment.documentsOnly"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].documentsOnly"
                      :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'documentsOnly' )"
                      label="Documents Only"
                      :labelWidth="100">
                  </CheckBox>
                  <CheckBox
                      v-if="!isEditCourierBooking"
                      style="margin-left: 30px;"
                      @click="props.modelValue.consignment.insuranceType = ''"
                      v-model="props.modelValue.consignment.insurance"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].insurance"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'insurance' )"
                      label="Insurance"
                      :label-width="50">
                  </CheckBox>
                  <MultiSearchDropDown
                      v-if="!isEditCourierBooking"
                      style="margin-left: 50px;"
                      :disable="!props.modelValue.consignment.insurance"
                      :width="180"
                      :labelWidth="50"
                      :disableFilter="true"
                      :allowCustomValue="false"
                      placeholder="Select Insurance Type"
                      label="Detail"
                      v-model="props.modelValue.consignment.insuranceType"
                      :selectPrimitiveValue="true"
                      :data-source="insuranceTypes">
                  </MultiSearchDropDown>
                </div>
                <div class="global-flex-layout">
                  <MultiSearchDropDown
                      v-if="store.state.userSettings.showCourierTerms && !isEditCourierBooking"
                      :width="400"
                      :labelWidth="110"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'termsOfShipment' )"
                      :allowCustomValue="true"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].termsOfShipment"
                      label="Terms"
                      @select="handleTermsDescription"
                      v-model="props.modelValue.consignment.termsOfShipment"
                      :selectPrimitiveValue="true"
                      :data-source="props.modelValue.metaData.terms">
                  </MultiSearchDropDown>
                  <StaticTooltip v-if="props.modelValue.consignment.termsOfShipment" :icon="false">
                    <template #content>
                      <div style="width: 300px">
                        {{ props.modelValue.consignment.termsDescription }}No Term selected
                      </div>
                    </template>
                    <i :class="store.state.globalHelpIcon"></i>
                  </StaticTooltip>
                </div>
              </div>
            </div>
            <div class="global-css_vertical-form-layout" style="margin: 20px 0 0 70px">
              <DropDownTextArea
                  :width="540"
                  :labelWidth="118"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'description' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].description"
                  v-model="props.modelValue.consignment.description"
                  label="Goods Description">
              </DropDownTextArea>
              <DropDownTextArea
                  :width="540"
                  :labelWidth="118"
                  :maxLength="42"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'comments' )"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].comments"
                  v-model="props.modelValue.consignment.comments"
                  label="Comments">
              </DropDownTextArea>
            <div class="global-flex-layout">
              <DateInput
                  :labelWidth="118"
                  v-model="props.modelValue.consignment.deliveryDate"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].deliveryDate"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'deliveryDate' )"
                  label="Deliver By">
                <el-date-picker
                    :clearable="false"
                    v-model="props.modelValue.consignment.deliveryDate"
                    value-format="DD-MMM-YY"/>
              </DateInput>
              <MultiSearchDropDown
                  style="margin-left: 225px;"
                  :width="100"
                  :label-width="90"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'deliveryTime' )"
                  placeholder="Select"
                  label="Delivery Time"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].deliveryTime"
                  v-model="props.modelValue.consignment.deliveryTime"
                  :selectPrimitiveValue="true"
                  :disableFilter="true"
                  :data-source="useTimeGenerator(24)">
              </MultiSearchDropDown>
            </div>
            <div class="global-flex-layout">
              <NumberOnlyInput
                  :width="100"
                  :maxLength="12"
                  :label-width="118"
                  label="Value"
                  :allowDecimal="true"
                  v-model="props.modelValue.consignment.value"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].value"
                  :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'value' )">
              </NumberOnlyInput>
              <MultiSearchDropDown
                  style="margin-left: 100px;"
                  :width="265"
                  :label-width="60"
                  :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'currency' )"
                  :allowCustomValue="false"
                  placeholder="Select or Enter"
                  label="Currency"
                  v-model:validation-fail="validationViewModel[props.validationViewModelSection].currency"
                  v-model="props.modelValue.consignment.currency"
                  :selectPrimitiveValue="true"
                  :data-source="props.modelValue.metaData.currencies">
              </MultiSearchDropDown>
            </div>
            <MultiSearchDropDown
                :width="245"
                :label-width="118"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'service' )"
                :allowCustomValue="false"
                placeholder="Select or Enter"
                label="Service Type"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].service"
                v-model="props.modelValue.consignment.service"
                :selectPrimitiveValue="true"
                :data-source="props.modelValue.metaData.serviceTypes">
            </MultiSearchDropDown>
          </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.dimensions" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <div id="flex-area-one">
              <div style="display: flex; align-items: center; margin-bottom: 20px;">
                <i class="fa-thin fa-box-open cmp-multi-booking_tile-icon" style="margin-right: 20px;"></i>
                <div class="label" style="display: flex; align-items: center;">Packaging Details
                  <span class="cmp-multi-booking_dims-message">
                  Pieces, Length, Width & Height are required to add a line of dims
                </span>
                </div>
              </div>
              <DimensionsEntry
                  @entry-update="getChargeableWeightCalculation"
                  :packaging="props.modelValue.metaData.packagingTypes"
                  :validationFail="props.modelValue.tileValidation.dimensions"
                  :requiredFields="props.modelValue.metaData.validatedFields"
                  v-model="props.modelValue.dimensions.multiLineDims">
              </DimensionsEntry>
            </div>
            <div id="flex-area-two" style="margin-left: 80px;">
              <div style="display:flex; align-items: center; padding:5px;" class="label">Dims Totals
                <span class="cmp-multi-booking_dims-message">You may enter your own pieces and weight.</span>
              </div>
              <div class="cmp-multi-booking_dims-totals">
                <div class="label" style="margin-bottom: 14px; display: flex; align-items: center;">
                  <span style="font-weight: normal;">Unit Of Measure</span>
                  <span style="margin-left: 58px;">
                      <el-switch
                          v-model="props.modelValue.unitOfMeasureSwitch"
                          size="small"
                          active-text="lbs / inches"
                          inactive-text="kg / cms"
                          style="--el-switch-on-color:var(--aqua-blue-definition); --el-switch-off-color: var(--aqua-blue-definition)"/>
                  </span>
                </div>
                <div style="display:flex;">
                  <NumberOnlyInput
                      :width="80"
                      :label-width="50"
                      label="Pieces"
                      v-model="props.modelValue.dimensions.totalPieces"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].totalPieces"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'totalPieces' )">
                  </NumberOnlyInput>
                  <NumberOnlyInput
                      style="margin-left: 14px;"
                      :width="80"
                      :disable="allowWeightEntry"
                      :label-width="58"
                      label="Weight"
                      v-model="props.modelValue.dimensions.totalWeight"
                      v-model:validation-fail="validationViewModel[props.validationViewModelSection].totalWeight"
                      :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'totalWeight' )">
                  </NumberOnlyInput>
                  <TextInput
                      style="margin-left: 14px;"
                      :width="80"
                      :readonly="true"
                      :label-width="110"
                      v-model="props.modelValue.dimensions.chargeableWeight"
                      label="Chargeable Weight">
                  </TextInput>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.documents" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <i class="fa-thin fa-folder-upload" style="font-size: 40px; color: #ecbf50">📂</i>
            <div class="cmp-multi-booking_upload-area-wrapper">
              <div class="cmp-multi-booking_upload-area-container">
                <div class="cmp-multi-booking_upload-button-area"><span class="label">Upload Documents</span>
                  <span class="cmp-multi-booking_dims-message"> Accepted file types: PDF, png, txt</span>
                  <div style="margin-top: 20px">
                    <Button text="Select" @click="handleUpload"></Button>
                    <span style="margin-left: 20px; font-size: 11px;">
                    You may add multiple files by using ctrl and click in the file window</span>
                  </div>
                  <div v-if="uploadFileSizeError" class="cmp-multi-booking_file-error-area">
                    {{ uploadFileSizeErrorMessage }}
                  </div>
                </div>
                <input :key="updateFileInput" v-show="false" ref="uploadButton" type="file" accept=".txt,.pdf,.png"
                       multiple @change="handleUploadedFiles($event.target.files)"/>
                <div class="cmp-multi-booking_uploaded-file-list-area">
                  <span class="label">Current Documents</span>
<!--                  <span class="cmp-multi-booking_dims-message">-->
<!--                  <i class="fa-sharp fa-solid fa-xmark" style="color: darkred;"></i> Removes from the list</span>-->
                  
                  <div class="cmp-multi-booking_uploaded-files-list">
                    
                    <div v-for="(item, index) in props.modelValue.files" class="cmp-multi-booking_uploaded-files-items">
                      {{ item.fileName }}
                      <div @click="handleRemoveDocument(index)">
                        <div title="REMOVE" style="font-size: 30px; margin-top: 13px; color: darkred; cursor: pointer">&#735</div>
                      </div>
                    </div>
                    <div v-if="props.modelValue.files.length === 0" class="global-no-data-placeholder"
                         style="display: flex; align-items: center; justify-content: center; height: 130px; font-size: 12px;">
                      No Current Documents
                      <LoadingSpinner :hide="!uploadFilesSpinner"></LoadingSpinner>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-if="props.modelValue.tileSelection.customs" class="cmp-multi-booking_drop-down">
          <div class="cmp-multi-booking_input-container cmp-multi-booking_add-border">
            <div class="cmp-multi-booking_customs-message">
              Note! The selections made and comments are not saved, but will show on the booking email.</div>
            <div v-if="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'customsStatus' )"
                 class="global-required-field cmp-multi-booking_validation-message">Please Select a Status
            </div>
            <div class="cmp-multi-booking_customs-wrapper">
              <div style="padding-top: 20px;">
                <CheckBox
                    v-model="props.modelValue.customsSelect.kimberlyCerts"
                    @change="handleCustomsStatus($event, 'KIMBERLY CERTS')"
                    :labelWidth="110" label="KIMBERLY CERTS">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.carnet"
                    @change="handleCustomsStatus($event, 'CARNET')"
                    label="CARNET"
                    :labelWidth="110">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.ipr"
                    @change="handleCustomsStatus($event, 'IPR')"
                    label="IPR"
                    :labelWidth="110">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.ti"
                    @change="handleCustomsStatus($event, 'TI')"
                    label="TI"
                    :labelWidth="110">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.opr"
                    @change="handleCustomsStatus($event, 'OPR')"
                    label="OPR"
                    :labelWidth="110">
                </CheckBox>
              </div>
              <div style="padding-top: 20px;">
                <CheckBox
                    v-model="props.modelValue.customsSelect.repair"
                    @change="handleCustomsStatus($event, 'REPAIR')"
                    :labelWidth="140"
                    label="REPAIR">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.cites"
                    @change="handleCustomsStatus($event, 'CITES')"
                    label="CITES"
                    :labelWidth="140">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.tibBondWarehouse"
                    @change="handleCustomsStatus($event, 'TIB BOND/WAREHOUSE')"
                    label="TIB BOND/WAREHOUSE"
                    :labelWidth="140">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.exportLicence"
                    @change="handleCustomsStatus($event, 'EXPORT LICENCE')"
                    label="EXPORT LICENCE"
                    :labelWidth="140">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.freeCirculation"
                    @change="handleCustomsStatus($event, 'FREE CIRCULATION')"
                    label="FREE CIRCULATION"
                    :labelWidth="140">
                </CheckBox>
                <CheckBox
                    v-model="props.modelValue.customsSelect.other"
                    @change="handleCustomsStatus($event, 'OTHER')"
                    label="OTHER"
                    :labelWidth="140">
                </CheckBox>
              </div>
              <div class="cmp-multi-booking_selected-customs"><span style="font-size: 13px;">Selected Status</span>
                <div class="cmp-multi-booking_selected-customs-wrapper">
                  <div class="cmp-multi-booking_selected-customs-items" v-for="each in props.modelValue.customs">
                    {{ each }}
                  </div>
                  <div class="cmp-multi-booking_selection-message"
                       v-if="props.modelValue.customs.length === 0">No Current Selections
                  </div>
                  <div class="cmp-multi-booking_validation-fail-message"
                       v-if="!props.modelValue.tileValidation.customs &&
                      useGetValidatedFields( props.modelValue.metaData.validatedFields,'customsStatus')">
                    Please Select a Customs Status
                  </div>
                </div>
              </div>

              <div><span :class="{'global-required-field'
             : useGetValidatedFields( props.modelValue.metaData.validatedFields,'customsComments')}"
                         style="font-size: 13px;">Customs Comments</span>
                <TextArea
                    v-model="props.modelValue.customsComments"
                    @input="props.modelValue.customsComments = $event"
                    v-model:validation-fail="validationViewModel[props.validationViewModelSection].customsComments"
                    style="margin-top: 10px;"
                    :height="150"
                    :width="352">
              </TextArea>

              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="cmp-multi-booking_summary-section">
        <StaticWidget title="Booking Summary" height="255" background="#fdfdfd">
          <div class="cmp-multi-booking_summary-wrapper">
            <div>
              <BookingSummaryDisplay displayType="address" :highlight="tileColor">
                <template #icon></template>
                <template #title>Shipper</template>
                <template v-if="props.modelValue.shipper.shipperName" #heading>
                  <div>{{ props.modelValue.shipper.shipperName.substring(0,35) ?? '' }}
                    <span>
                      <StaticTooltip :icon="false">
                  <template #content>
                    <div style="min-width: 100px">
                      <div style="color: var(--aqua-blue-definition); margin-bottom: 5px;">Address Information</div>
                      <div><strong>{{ props.modelValue.shipper.shipperName }}</strong></div>
                      <div>{{ props.modelValue.shipper.shipperAddressLine1 }}</div>
                      <div>{{ props.modelValue.shipper.shipperAddressLine2 }}</div>
                      <div>{{ props.modelValue.shipper.shipperCity }}</div>
                      <div>{{ props.modelValue.shipper.shipperPostcode }}</div>
                      <div>{{ props.modelValue.shipper.shipperCounty }}</div>
                      <div>{{ props.modelValue.shipper.shipperCountry }}</div>
                      <div style="margin-top: 3px; color: #696969;">VAT No.{{props.modelValue.shipper.shipperVatNumber }}</div>
                    </div>
                  </template>
              <i :class="store.state.globalHelpIcon" style="margin-left: 6px;"></i>
                </StaticTooltip>
                  </span>
                  </div>
                </template>
                <template #second-content v-if="props.modelValue.shipper.shipperContactFirstName">
                  <span><img height="12" width="12" src="@/assets/icons/user_small.png"></span>
                  <span> {{ props.modelValue.shipper.shipperContactFirstName + ' '
                  + props.modelValue.shipper.shipperContactLastName }}</span>
                </template>
                <template #third-content v-if="props.modelValue.shipper.shipperContactPhone">
                  <span style="margin-top: 3px"> <img height="10" width="10" src="@/assets/icons/phone_small.png"></span>
                  <span> {{ props.modelValue.shipper.shipperContactPhone }}</span>
                </template>
                <template #fourth-content v-if="props.modelValue.shipper.shipperContactEmail">
                  <span> <img height="12" width="12" src="@/assets/icons/email_small.png"></span>
                  <span> {{ props.modelValue.shipper.shipperContactEmail }}</span>
                </template>
                <template #placeholder v-if="!useCheckFieldsForData(props.modelValue.shipper)">
                  <div style="margin-top: 14px;"><img :src="getPlaceholder"/></div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div>
              <BookingSummaryDisplay displayType="address" :highlight="tileColor">
                <template #icon></template>
                <template #title>Consignee</template>
                <template v-if="props.modelValue.consignee.consigneeName" #heading>
                  <div>{{ props.modelValue.consignee.consigneeName.substring(0.35) ?? '' }}
                    <span>
                      <StaticTooltip :icon="false">
                  <template #content>
                    <div style="min-width: 100px"><div style="color: var(--aqua-blue-definition); margin-bottom: 5px;">Address Information</div>
                      <div><strong>{{ props.modelValue.consignee.consigneeName }}</strong></div>
                      <div>{{ props.modelValue.consignee.consigneeAddressLine1 }}</div>
                      <div>{{ props.modelValue.consignee.consigneeAddressLine2 }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCity }}</div>
                      <div>{{ props.modelValue.consignee.consigneePostcode }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCounty }}</div>
                      <div>{{ props.modelValue.consignee.consigneeCountry }}</div>
                      <div style="margin-top: 3px; color: #696969;">VAT No.{{props.modelValue.consignee.consigneeVatNumber }}</div>
                    </div>
                  </template>
              <i :class="store.state.globalHelpIcon" style="margin-left: 6px;"></i>
                </StaticTooltip>
                  </span>
                  </div>
                </template>
                <template #second-content v-if="props.modelValue.consignee.consigneeContactFirstName">
                  <span> <img height="12" width="12" src="@/assets/icons/user_small.png"></span>
                  <span> {{ props.modelValue.consignee.consigneeContactFirstName + ' ' + props.modelValue.consignee.consigneeContactLastName }}</span>
                </template>
                <template #third-content v-if="props.modelValue.consignee.consigneeContactPhone">
                  <span> <img height="10" width="10" src="@/assets/icons/phone_small.png"></span>
                  <span> {{ props.modelValue.consignee.consigneeContactPhone }}</span>
                </template>
                <template #fourth-content v-if="props.modelValue.consignee.consigneeContactEmail">
                  <span> <img height="12" width="12" src="@/assets/icons/email_small.png"></span>
                  <span> {{ props.modelValue.consignee.consigneeContactEmail }}</span>
                </template>
                <template #placeholder v-if="!useCheckFieldsForData(props.modelValue.consignee)">
                  <div style="margin-top: 14px;"><img :src="getPlaceholder"/></div>
                </template>
              </BookingSummaryDisplay>
            </div>
          </div>

          <div style="margin-left: 20px;">
            <div v-if="props.validationViewModelSection === 'freightBooking'">
              <BookingSummaryDisplay :width="1450" :highlight="tileColor">
                <template #icon></template>
                <template #title>Shipment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Your Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.shipment.shipmentRef">
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{props.modelValue.shipment.shipmentRef}}</div>
                          </template>
                          {{useCreateSubstringText(props.modelValue.shipment.shipmentRef, 35)}}
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Shipper Reference
                  </div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.shipment.shipmentShipperRef">
                    <StaticTooltip :icon="false">
                      <template #content>
                        <div style="max-width: 400px;">{{props.modelValue.shipment.shipmentShipperRef}}</div>
                      </template>
                      {{useCreateSubstringText(props.modelValue.shipment.shipmentShipperRef, 35)}}
                    </StaticTooltip>
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Consignee Reference
                  </div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.shipment.shipmentConsigneeRef">
                    <StaticTooltip :icon="false">
                      <template #content>
                        <div style="max-width: 400px;">{{props.modelValue.shipment.shipmentConsigneeRef}}</div>
                      </template>
                      {{useCreateSubstringText(props.modelValue.shipment.shipmentConsigneeRef, 35)}}
                    </StaticTooltip>
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #4>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Billing Party</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.shipment.shipmentBillingParty">
                    {{ props.modelValue.shipment.shipmentBillingParty }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>

            <div v-if="props.validationViewModelSection === 'freightBooking'">
              <BookingSummaryDisplay :width="1450" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Carrier</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.shipment.shipmentCarrier">
                        {{ props.modelValue.shipment.shipmentCarrier }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Service Type</div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.shipment.shipmentServiceType">
                    {{ props.modelValue.shipment.shipmentServiceType }}
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Pickup By</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 100px"
                           v-if="props.modelValue.shipment.shipmentPickupDate">
                        {{ props.modelValue.shipment.shipmentPickupDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Delivery Date
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.shipment.shipmentDeliveryDate">
                        {{props.modelValue.shipment.shipmentDeliveryDate}}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Value</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 80px"
                           v-if="props.modelValue.shipment.shipmentValue">
                        {{ props.modelValue.shipment.shipmentValue }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:80px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.shipment.shipmentCurrency">
                        {{ props.modelValue.shipment.shipmentCurrency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:120px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="props.validationViewModelSection === 'courierBooking'">
              <BookingSummaryDisplay :width="1450" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-person-dolly-empty" style="margin-left: 5px;"></i></template>
                <template #title>Consignment</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Your Reference
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.customerRef">
                        {{useCreateSubstringText(props.modelValue.consignment.customerRef, 30)}}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{props.modelValue.consignment.customerRef}}</div>
                          </template>
                          <span><i class="fa fa-info-circle" style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Additional Reference
                  </div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.consignment.customerRefTwo">
                    {{useCreateSubstringText(props.modelValue.consignment.customerRefTwo, 34)}}
                    <StaticTooltip :icon="false">
                      <template #content>
                        <div style="max-width: 400px;">{{props.modelValue.consignment.customerRefTwo}}</div>
                      </template>
                      <span><i class="fa fa-info-circle" style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                    </StaticTooltip>
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <div class="data-placeholder"></div>
                  </div>
                </template>
                <template #3>
                  <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Documents Only
                  </div>
                  <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                       v-if="props.modelValue.consignment.documentsOnly">
                    <i class="fa-solid fa-circle-check" style="color: #3f9968"></i>
                  </div>
                  <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                    <i class="fa-solid fa-circle-xmark" style="color: #993f3f"></i>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Value</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 60px"
                           v-if="props.modelValue.consignment.value">
                        {{ props.modelValue.consignment.value }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:60px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Currency
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.currency">
                        {{ props.modelValue.consignment.currency }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:160px"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
            <div v-if="props.validationViewModelSection === 'courierBooking'">
              <BookingSummaryDisplay :width="1450" :highlight="tileColor">
                <template #icon><i class="fa-duotone fa-truck-ramp-box"></i></template>
                <template #title>Movement</template>
                <template #1>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Service Type</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.service">
                        {{ props.modelValue.consignment.service }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #2>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Collect By</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 120px"
                           v-if="props.modelValue.consignment.collectByDate">
                        {{ props.modelValue.consignment.collectByDate }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:120px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Collection Time
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.collectByTime">
                        {{props.modelValue.consignment.collectByTime}}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #3>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Deliver By</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data" style="width: 120px"
                           v-if="props.modelValue.consignment.deliveryDate">
                        {{ props.modelValue.consignment.deliveryDate}}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:120px"></div>
                      </div>
                    </div>
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Delivery Time
                      </div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.deliveryTime">
                        {{ props.modelValue.consignment.deliveryTime }}
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder" style="width:100px"></div>
                      </div>
                    </div>
                  </div>
                </template>
                <template #4>
                  <div style="display: flex; gap:50px;">
                    <div>
                      <div class="cmp-multi-booking_summary-label" style="padding: 20px 10px 2px 0;">Comments</div>
                      <div class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data"
                           v-if="props.modelValue.consignment.comments">
                        {{useCreateSubstringText(props.modelValue.consignment.comments, 30)}}
                        <StaticTooltip :icon="false">
                          <template #content>
                            <div style="max-width: 400px;">{{props.modelValue.consignment.comments}}</div>
                          </template>
                          <span><i class="fa fa-info-circle" style="color: var(--info-icon-color); margin-left: 6px;"></i></span>
                        </StaticTooltip>
                      </div>
                      <div v-else class="cmp-multi-booking_summary-label cmp-multi-booking_summary-data">
                        <div class="data-placeholder"></div>
                      </div>
                    </div>
                  </div>
                </template>
              </BookingSummaryDisplay>
            </div>
          </div>
        </StaticWidget>
      </div>
    </StaticWidget>
    <Modal
        v-model="props.modelValue.openShipperModal"
        :showClose="false"
        @close="closeShipperModel()"
        :title="modelValue.isEditShipperAddress ? 'Edit Shipper': 'New Shipper'"
        width="510px"
        height="380px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperName"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperName"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperName' )"
              :label-top="true"
              label="Shipper Name">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperAddressLine1"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperAddressLine1"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperAddressLine1' )"
              :label-top="true"
              label="Address Line 1">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newShipperCompany.shipperAddressLine2"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperAddressLine2"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperAddressLine2' )"
              :label-top="true"
              label="Address Line 2">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="280"
              v-model="props.modelValue.newShipperCompany.shipperCity"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCity"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperCity' )"
              :label-top="true"
              label="City">
          </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="10"
                v-model="props.modelValue.newShipperCompany.shipperPostcode"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperPostcode"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperPostcode' )"
                :width="280"
                :label-top="true"
                label="Post Code / ZIP">
            </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="50"
                v-model="props.modelValue.newShipperCompany.shipperCounty"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCounty"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperCounty' )"
                :width="280"
                :label-top="true"
                label="County / State">
            </TextInput>
          <MultiSearchDropDown
              v-if="props.modelValue.metaData.countries.length > 0"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperCountry' )"
              :allowCustomValue="false"
              placeholder="Select or Search...."
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperCountry"
              label="Country"
              :width="280"
              v-model="props.modelValue.newShipperCompany.shipperCountry"
              :selectPrimitiveValue="true"
              :label-top="true"
              :data-source="props.modelValue.metaData.countries">
          </MultiSearchDropDown>
          <div style="display: flex; align-items: center;">
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="50"
                :width="280"
                v-model="props.modelValue.newShipperCompany.shipperVatNumber"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperVatNumber"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperVatNumber' )"
                :label-top="true"
                label="VAT Number">
            </TextInput>
            <div @click="useUnSavedShipperAddress(false, 'newShipperCompany')" class="cmp-multi-booking_address-link">Use This Address</div>
            <div title="Use this address for the booking only, this will not be saved in your shipper list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newShipperCompany.id !== 0 "
            @click="handleNewCompanySave('newShipperCompany')"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newShipperCompany.id === 0 "
            @click="handleNewCompanySave('newShipperCompany')"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            text="Save">
        </Button>
      </template>
    </Modal>

    <Modal
        v-model="props.modelValue.openShipperContactModal"
        :showClose="false"
        :title="modelValue.newShipperCompany.shipperContactId ? 'Edit Shipper Contact': 'New Shipper Contact'"
        width="530px"
        height="200px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <div class="global-flex-layout">
            <MultiSearchDropDown
                :key="updateShipperContacts"
                placeholder="Title"
                :maxLength="50"
                :width="60"
                label="Name"
                :label-top="true"
                :labelWidth="85"
                :allowCustomValue="false"
                :disableFilter="true"
                :selectPrimitiveValue="true"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactTitle"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactTitle' )"
                v-model="props.modelValue.newShipperCompany.shipperContactTitle"
                :data-source="store.state.contactTitleSelection">
            </MultiSearchDropDown>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :width="140"
                :maxLength="20"
                label="First Name"
                :label-top="true"
                v-model="props.modelValue.newShipperCompany.shipperContactFirstName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactFirstName"
                :requiredField="true">
            </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="25"
                :width="190"
                label="Last Name"
                :label-top="true"
                v-model="props.modelValue.newShipperCompany.shipperContactLastName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactLastName"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactLastName' )">
            </TextInput>
            <StaticTooltip :icon="false">
              <template #content>
                Clear Contact Fields
              </template>
              <img @click="useClearShipperContact(props.modelValue.newShipperCompany)"
                   height="20"
                   width="20"
                   src="@/assets/icons/akar/akar-icons--trash-can.png">
            </StaticTooltip>
          </div>
          <TextInput
              :maxLength="30"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newShipperCompany.shipperContactPhone"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactPhone"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'shipperContactPhone' )"
              :label-top="true"
              label="Telephone">
          </TextInput>
          <TextInput
              :maxLength="50"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newShipperCompany.shipperContactEmail"
              :validationText="['Required', 'Invalid Email']"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactEmail"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperContactEmail' )"
              :label-top="true"
              label="Email">
          </TextInput>
          <div style="display: flex; align-items: center;">
            <TextInput
                :maxLength="50"
                :labelWidth="85"
                :width="300"
                v-model="props.modelValue.newShipperCompany.shipperContactDepartment"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].shipperContactDepartment"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'shipperContactDepartment' )"
                :label-top="true"
                label="Department">
            </TextInput>
            <div @click="useUnSavedShipperContact()" class="cmp-multi-booking_address-link">Use This Contact</div>
            <div title="Use this contact for the booking only, this will not be saved in your shipper contact list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newShipperCompany.shipperContactId !== 0"
            @click="saveNewShipperContact(true)"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newShipperCompany.shipperContactId === 0"
            @click="saveNewShipperContact(false)"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            :disabled="props.modelValue.shipper.id === 0"
            title="You may save this contact to any newly created, or selected shipper"
            text="Save">
        </Button>
      </template>
    </Modal>


    <Modal
        v-model="props.modelValue.openConsigneeModal"
        :showClose="false"
        @close="closeConsigneeModel()"
        :title="modelValue.isEditConsigneeAddress ? 'Edit Consignee': 'New Consignee'"
        width="510px"
        height="380px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeName"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeName"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeName' )"
              :label-top="true"
              label="Consignee Name">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeAddressLine1"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeAddressLine1"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeAddressLine1' )"
              :label-top="true"
              label="Address Line 1">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="400"
              v-model="props.modelValue.newConsigneeCompany.consigneeAddressLine2"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeAddressLine2"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeAddressLine2' )"
              :label-top="true"
              label="Address Line 2">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              :width="280"
              v-model="props.modelValue.newConsigneeCompany.consigneeCity"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCity"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeCity' )"
              :label-top="true"
              label="City">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="10"
              v-model="props.modelValue.newConsigneeCompany.consigneePostcode"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneePostcode"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneePostcode' )"
              :width="280"
              :label-top="true"
              label="Post Code / ZIP">
          </TextInput>
          <TextInput
              v-if="props.modelValue.metaData.validatedFields"
              :maxLength="50"
              v-model="props.modelValue.newConsigneeCompany.consigneeCounty"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCounty"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeCounty' )"
              :width="280"
              :label-top="true"
              label="County / State">
          </TextInput>
          <MultiSearchDropDown
              v-if="props.modelValue.metaData.countries.length > 0"
              :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeCountry' )"
              :allowCustomValue="false"
              placeholder="Select or Search...."
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeCountry"
              label="Country"
              :width="280"
              v-model="props.modelValue.newConsigneeCompany.consigneeCountry"
              :selectPrimitiveValue="true"
              :label-top="true"
              :data-source="props.modelValue.metaData.countries">
          </MultiSearchDropDown>
          <div style="display: flex; align-items: center;">
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="50"
                :width="280"
                v-model="props.modelValue.newConsigneeCompany.consigneeVatNumber"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeVatNumber"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeVatNumber' )"
                :label-top="true"
                label="VAT Number">
            </TextInput>
            <div @click="useUnSavedConsigneeAddress(false, 'newShipperCompany')" class="cmp-multi-booking_address-link">Use This Address</div>
            <div title="Use this address for the booking only, this will not be saved in your shipper list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newConsigneeCompany.id !== 0"
            @click="handleNewCompanySave('newConsigneeCompany')"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newConsigneeCompany.id === 0 "
            @click="handleNewCompanySave('newConsigneeCompany')"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            text="Save">
        </Button>
      </template>
    </Modal>


    <Modal
        v-model="props.modelValue.openConsigneeContactModal"
        :showClose="false"
        @close="closeShipperModel()"
        :title="modelValue.newConsigneeCompany.consigneeContactId ? 'Edit Consignee Contact': 'New Consignee Contact'"
        width="530px"
        height="160px">
      <template #body>
        <div class="cmp-multi-booking_address-entry global-css_horizontal-form-layout-label-top">
          <div class="global-flex-layout">
            <MultiSearchDropDown
                :key="updateConsigneeContacts"
                placeholder="Title"
                :maxLength="50"
                :width="60"
                label="Name"
                :label-top="true"
                :labelWidth="85"
                :allowCustomValue="false"
                :disableFilter="true"
                :selectPrimitiveValue="true"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactTitle"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactTitle' )"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactTitle"
                :data-source="store.state.contactTitleSelection">
            </MultiSearchDropDown>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :width="140"
                :maxLength="20"
                label="First Name"
                :label-top="true"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactFirstName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactFirstName"
                :requiredField="true">
            </TextInput>
            <TextInput
                v-if="props.modelValue.metaData.validatedFields"
                :maxLength="25"
                :width="190"
                label="Last Name"
                :label-top="true"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactLastName"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactLastName"
                :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactLastName' )">
            </TextInput>
            <StaticTooltip :icon="false">
              <template #content>
                Clear Contact Fields
              </template>
              <img @click="useClearConsigneeContact(props.modelValue.newConsigneeCompany)"
                   height="20"
                   width="20"
                   src="@/assets/icons/akar/akar-icons--trash-can.png">
            </StaticTooltip>
          </div>
          <TextInput
              :maxLength="30"
              :labelWidth="85"
              :width="300"
              v-model="props.modelValue.newConsigneeCompany.consigneeContactPhone"
              v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactPhone"
              :requiredField="useGetValidatedFields( props.modelValue.metaData.validatedFields, 'consigneeContactPhone' )"
              :label-top="true"
              label="Telephone">
          </TextInput>
          <div style="display: flex; align-items: center;">
            <TextInput
                :maxLength="50"
                :labelWidth="85"
                :width="300"
                v-model="props.modelValue.newConsigneeCompany.consigneeContactEmail"
                :validationText="['Required', 'Invalid Email']"
                v-model:validation-fail="validationViewModel[props.validationViewModelSection].consigneeContactEmail"
                :requiredField="useGetValidatedFields(props.modelValue.metaData.validatedFields, 'consigneeContactEmail' )"
                :label-top="true"
                label="Email">
            </TextInput>
            <div @click="useUnSavedConsigneeContact()" class="cmp-multi-booking_address-link">Use This Contact</div>
            <div title="Use this contact for the booking only, this will not be saved in your shipper contact list."
                 class="global-info-icon">
            </div>
          </div>
        </div>
      </template>
      <template #footer>
        <Button
            v-if="props.modelValue.newConsigneeCompany.consigneeContactId !== 0"
            @click="saveNewConsigneeContact(true)"
            icon="<i class='fa-light fa-circle-plus'></i>"
            text="Update">
        </Button>
        <Button
            v-if="props.modelValue.newConsigneeCompany.consigneeContactId === 0"
            @click="saveNewConsigneeContact(false)"
            :disabled="props.modelValue.consignee.id === 0"
            icon="<i class='fa-light fa-floppy-disk'></i>"
            title="You may save this contact to any newly created, or selected consignee"
            text="Save">
        </Button>
      </template>
    </Modal>

  </div>
</template>

<style lang="scss" scoped>

@import '@/css/booking/multi-booking.scss';

.cmp-multi-booking {

  &_add-border {
    border-left: 6px solid var(--left-border-highlight);
  }

  &_customs-message {
    position: absolute;
    top: 17px;
    font-size: 12px;
    color: grey;
  }
}

</style>
